import React, { useState, useEffect, useCallback } from 'react';
import { Select, Button, Modal as AntModal, Spin, Empty, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../utils/api';
import CreateColorPage from '../pages/CreateColor'; // CreateColor sayfasını import ediyoruz
import { toast } from 'react-toastify';

export interface Color {
  _id: string;
  name: string;
  firmCode?: string;
  tcxCode?: string;
  hexCode?: string;
  notes?: string;
}

interface ColorSelectorProps {
  selectedColor: Color | null;
  onColorSelect: (color: Color) => void;
  className?: string;
}

const ColorSelector: React.FC<ColorSelectorProps> = ({ selectedColor, onColorSelect, className }) => {
  const [colors, setColors] = useState<Color[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchColors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/colors');
      setColors(response.data || []);
    } catch (error) {
      console.error("Renkler yüklenirken hata oluştu:", error);
      toast.error('Renkler yüklenirken bir hata oluştu.');
      setColors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchColors();
  }, [fetchColors]);

  const handleColorCreated = async (newColor: Color) => { // Fonksiyonu async yap
    setIsModalOpen(false); // Modalı hemen kapat
    try {
      await fetchColors(); // Renk listesinin güncellenmesini bekle
      // Güncellenmiş listeden sonra, yeni oluşturulan rengi seçili olarak bildir.
      // Bu, ProductEdit'in doğru renk objesine sahip olmasını sağlar.
      onColorSelect(newColor); 
      toast.success(`Yeni renk "${newColor.name}" seçildi.`);
    } catch (error) {
      console.error("Yeni renk oluşturulduktan sonra renk listesi güncellenirken veya renk seçilirken hata:", error);
      toast.error('Yeni renk seçilirken bir sorun oluştu.');
      // Hata durumunda bile, yeni rengi seçmeye çalışabiliriz,
      // ancak liste güncel olmayabilir.
      // onColorSelect(newColor); // Opsiyonel: Hata olsa bile seçimi dene
    }
  };

  const filteredColors = colors.filter(color =>
    color.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    color.firmCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    color.tcxCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    color.hexCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Select
        showSearch
        value={selectedColor?._id || undefined}
        onChange={(value) => {
          console.log('[DEBUG] Select değeri değişti:', value);
          const selectedColor = colors.find(c => c._id === value);
          console.log('[DEBUG] Bulunan renk:', selectedColor);
          if (selectedColor) {
            onColorSelect(selectedColor);
          }
        }}
        onSearch={setSearchTerm}
        placeholder="Renk seçin veya arayın"
        loading={loading}
        className={className || 'w-full'}
        filterOption={false}
        dropdownRender={(menu) => (
          <>
            {menu}
            <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8, borderTop: '1px solid #f0f0f0' }}>
              <Button type="link" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)} style={{ flex: 'auto' }}>
                Yeni Renk Ekle
              </Button>
            </div>
          </>
        )}
        notFoundContent={loading ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Renk bulunamadı" />}
      >
        {filteredColors.map(color => (
          <Select.Option key={color._id} value={color._id} label={color.name}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {color.hexCode && (
                <Tag color={`#${color.hexCode}`} style={{ marginRight: 8, minWidth: '20px', height: '20px', border: '1px solid #d9d9d9' }}>
                  &nbsp;
                </Tag>
              )}
              <span>{color.name}</span>
              {color.firmCode && <span style={{ marginLeft: 8, color: '#888' }}>({color.firmCode})</span>}
              {color.tcxCode && <span style={{ marginLeft: 8, color: '#aaa' }}>TCX: {color.tcxCode}</span>}
            </div>
          </Select.Option>
        ))}
      </Select>

      <AntModal
        title="Yeni Renk Oluştur"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null} // CreateColorPage kendi butonlarını içeriyor
        destroyOnClose // Modal kapandığında içindeki state'leri sıfırla
        width={600}
      >
        <CreateColorPage
          isDrawerMode // Modal içinde olduğunu belirtmek için (stil ayarları için)
          onSuccess={handleColorCreated}
          onCancel={() => setIsModalOpen(false)}
        />
      </AntModal>
    </>
  );
};

export default ColorSelector;